import {
  StructuredTableColumn,
  StructuredTableColumnSummary,
  TagValueStructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {CreateProcessButtonQuery} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {
  isRequiredLinkCustomSchemaInterface,
  requiredLinkToSchemaId,
  getRequiredLinkFolderItemType,
} from 'schemas/fields/SchemaFieldConstants';

type ProcessTaskSchema = CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

function getDestinationColumnSummary(
  column: StructuredTableColumn,
  schema: ProcessTaskSchema
): {
  destinationSchemaIdConstraint: string | null;
  destinationFolderItemType: string | null;
  destinationSchemaFieldIsSnapshot: boolean;
  destinationIsFieldset: boolean;
} {
  const matchingField = schema.fields.find(
    (field: ClientSchemaField) => column.schemaFieldApiId === field.apiId
  );

  let requiredLinkSchemaId: string | null = null;
  let requiredLinkFolderItemType: string | null = null;
  if (matchingField?.requiredLink != null) {
    requiredLinkSchemaId = requiredLinkToSchemaId(matchingField.requiredLink);
    requiredLinkFolderItemType = getRequiredLinkFolderItemType(matchingField.requiredLink);
  }

  return {
    destinationSchemaIdConstraint: requiredLinkSchemaId,
    destinationFolderItemType: requiredLinkFolderItemType,
    destinationSchemaFieldIsSnapshot: matchingField?.isSnapshot ?? false,
    destinationIsFieldset: isRequiredLinkCustomSchemaInterface(matchingField?.requiredLink),
  };
}

/**
 * A fieldset destination is satisfied when every selected item's schema implements that fieldset
 * (`implementedInterfaceIds`); any other destination requires exact concrete-schema-id equality.
 */
function sourceSatisfiesSchemaIdConstraint(
  columnSummary: StructuredTableColumnSummary,
  destinationSchemaIdConstraint: string,
  destinationIsFieldset: boolean
): boolean {
  if (destinationIsFieldset) {
    return columnSummary.implementedInterfaceIds?.has(destinationSchemaIdConstraint) ?? false;
  }
  return columnSummary.schemaIdConstraint === destinationSchemaIdConstraint;
}

export function findMatchingDestinationColumnIndices(
  columnSummary: StructuredTableColumnSummary,
  destinationSchema: ProcessTaskSchema,
  destinationTable: UnknownStructuredTable
): Array<number> {
  const matchingColIndices: Array<number> = [];
  for (const [colIndex, destinationColumnCandidate] of destinationTable.columns.entries()) {
    const {
      destinationSchemaIdConstraint,
      destinationFolderItemType,
      destinationSchemaFieldIsSnapshot,
      destinationIsFieldset,
    } = getDestinationColumnSummary(destinationColumnCandidate, destinationSchema);
    // Check if the destination column satisfies any of the source column requirements
    for (const destinationColumnRequirement of columnSummary.validDestinationColumnRequirements) {
      if (destinationSchemaFieldIsSnapshot) {
        continue;
      } else if (destinationColumnRequirement.columnType !== destinationColumnCandidate.columnType) {
        continue;
      } else if (
        destinationColumnRequirement?.schemaFieldType != null &&
        destinationColumnRequirement.schemaFieldType !==
          (destinationColumnCandidate as TagValueStructuredTableColumn).schemaFieldType
      ) {
        continue;
      } else if (
        destinationFolderItemType != null &&
        destinationColumnRequirement.folderItemType !== destinationFolderItemType
      ) {
        // Fieldset destinations have a null folderItemType, so this gate only affects concrete schemas.
        continue;
      } else if (
        destinationSchemaIdConstraint != null &&
        !sourceSatisfiesSchemaIdConstraint(
          columnSummary,
          destinationSchemaIdConstraint,
          destinationIsFieldset
        )
      ) {
        continue;
      }

      matchingColIndices.push(colIndex);
    }
  }
  return matchingColIndices;
}

export default function findMatchingDestinationColumnIndex(
  columnSummary: StructuredTableColumnSummary,
  destinationSchema: ProcessTaskSchema,
  destinationTable: UnknownStructuredTable
): number | null {
  let matchingColIndex = null;
  for (const [colIndex, destinationColumnCandidate] of destinationTable.columns.entries()) {
    const {
      destinationSchemaIdConstraint,
      destinationFolderItemType,
      destinationSchemaFieldIsSnapshot,
      destinationIsFieldset,
    } = getDestinationColumnSummary(destinationColumnCandidate, destinationSchema);
    // Check if the destination column satisfies any of the source column requirements
    for (const destinationColumnRequirement of columnSummary.validDestinationColumnRequirements) {
      if (destinationSchemaFieldIsSnapshot) {
        continue;
      } else if (destinationColumnRequirement.columnType !== destinationColumnCandidate.columnType) {
        continue;
      } else if (
        destinationColumnRequirement?.schemaFieldType != null &&
        destinationColumnRequirement.schemaFieldType !==
          (destinationColumnCandidate as TagValueStructuredTableColumn).schemaFieldType
      ) {
        continue;
      } else if (
        destinationFolderItemType != null &&
        destinationColumnRequirement.folderItemType !== destinationFolderItemType
      ) {
        // Fieldset destinations have a null folderItemType, so this gate only affects concrete schemas.
        continue;
      } else if (
        destinationSchemaIdConstraint != null &&
        !sourceSatisfiesSchemaIdConstraint(
          columnSummary,
          destinationSchemaIdConstraint,
          destinationIsFieldset
        )
      ) {
        continue;
      }

      if (
        destinationSchemaIdConstraint != null &&
        !destinationIsFieldset &&
        columnSummary.schemaIdConstraint === destinationSchemaIdConstraint
      ) {
        // Prefer an exact concrete-schema match; a fieldset match is weaker and only fills the fallback.
        return colIndex;
      } else if (matchingColIndex == null) {
        matchingColIndex = colIndex;
      }
    }
  }
  return matchingColIndex;
}
